import React, { useState, useEffect, useRef, useCallback } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import {
  parseLoopFeed,
  getInteractivesDataAttr,
} from "../../../utils/interactivesHelper";
import Grid from "../styles/Grid";
import RssCard from "../cards/RssCard";
import TwitterCard from "../cards/TwitterCard";
import InstagramCard from "../cards/InstagramCard";
import YoutubeCard from "../cards/YoutubeCard";
import AdWidget from "../adWidgets/AdWidget";
import MatchTimeline from "../../football/match/MatchTimeline";
import ComponentAdRenderer from "../adWidgets/ComponentAdRenderer";
import FeedSlider from "../slider/FeedSlider";

// Default 300x250 ad slot — rendered as a placeholder when no ad src is provided
const DEFAULT_AD_CONFIG = [
  { ratio: "300x250", src: "", link: "", insertAfter: 3 },
];

const PAGE_SIZE = 20;

/**
 * Appends limit + offset query params to a feed URL.
 * Works whether the URL already has query params or not.
 */
const withPagination = (url, limit, offset) => {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}limit=${limit}&offset=${offset}`;
};

/**
 * InteractivesHost — orchestrates the full interactives widget.
 * Assembles a Grid containing RSS cards interleaved with ad slots (300x250 by default).
 * Supports infinite pagination: when the FeedSlider reaches the last slide it
 * automatically fetches the next page from the loop-feed API.
 *
 * Props:
 *  feedUrl       - Loop-feed API base URL
 *  adConfig      - Array of ad objects: [{ ratio, src, link, insertAfter }]
 *  matchHeadConfig - Match head widget config
 *  layout        - Min column width in px for Grid (default: 300)
 *  gap           - Grid gap in px (default: 16)
 *  maxItems      - Max items for the initial grid render (default: 20)
 *  loadCSS       - Load component styles dynamically
 *  dataClient    - If true, reads feedUrl from DOM data attribute
 *  ServerData    - Pre-fetched RSS items array (SSR mode)
 *  dictionary    - i18n strings object
 */
const InteractivesHost = ({
  feedUrl: feedUrlProp = "",
  adConfig = DEFAULT_AD_CONFIG,
  layout = 300,
  gap = 16,
  maxItems = PAGE_SIZE,
  loadCSS = true,
  dataClient = false,
  ServerData = null,
  dictionary = {},
  sliderRatio = "",
  showCaption = true,
  containerEl,
}) => {
  const [items, setItems]       = useState(ServerData || []);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [sliderIndex, setSliderIndex] = useState(null);

  // Pagination state
  const [offset, setOffset]     = useState(0);
  const [hasMore, setHasMore]   = useState(true);
  const loadingMore             = useRef(false); // prevents duplicate in-flight fetches

  useEffect(() => {
    if (loadCSS === true || loadCSS === "true") {
      loadStyles("interactives");
    }
  }, [loadCSS]);

  // Resolve the base feed URL (from prop or DOM attribute)
  const resolveFeedUrl = useCallback(() => {
    return dataClient
      ? getInteractivesDataAttr("feedurl", containerEl) || feedUrlProp
      : feedUrlProp;
  }, [feedUrlProp, dataClient, containerEl]);

  // Initial load
  useEffect(() => {
    if (ServerData && !dataClient) return;

    const feedUrl = resolveFeedUrl();
    if (!feedUrl) return;

    setLoading(true);
    setError(null);

    parseLoopFeed(withPagination(feedUrl, PAGE_SIZE, 0))
      .then((parsed) => {
        setItems(parsed);
        setOffset(PAGE_SIZE);
        // If the API returned fewer items than requested it has no more pages
        setHasMore(parsed.length >= PAGE_SIZE);
        setLoading(false);
      })
      .catch((err) => {
        console.error("[InteractivesHost] Feed fetch error:", err);
        setError(dictionary.feedError || "Failed to load feed.");
        setLoading(false);
      });
  }, [feedUrlProp, dataClient]);

  // Called by FeedSlider when the user is near the last slide
  const fetchMore = useCallback(() => {
    if (loadingMore.current || !hasMore) return;

    const feedUrl = resolveFeedUrl();
    if (!feedUrl) return;

    loadingMore.current = true;

    parseLoopFeed(withPagination(feedUrl, PAGE_SIZE, offset))
      .then((parsed) => {
        setItems((prev) => {
          if (parsed.length === 0) {
            setHasMore(false);
            return prev;
          }
          // Deduplicate by link — if every returned item already exists, we're at the end
          const existingLinks = new Set(prev.map((i) => i.link));
          const newItems = parsed.filter((i) => !existingLinks.has(i.link));
          if (newItems.length === 0) {
            setHasMore(false);
            return prev;
          }
          setOffset((o) => o + PAGE_SIZE);
          setHasMore(parsed.length >= PAGE_SIZE);
          return [...prev, ...newItems];
        });
        loadingMore.current = false;
      })
      .catch(() => {
        loadingMore.current = false;
      });
  }, [offset, hasMore, resolveFeedUrl]);

  if (loading) {
    return (
      <div className="interactives-host interactives-host--loading">
        <span>{dictionary.loading || "Loading…"}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="interactives-host interactives-host--error">
        <span>{error}</span>
      </div>
    );
  }

  if (!items.length) return null;

  /**
   * Build the grid children: interleave ads after every N RSS cards.
   */
  const buildChildren = () => {
    const children = [];
    let adIndex = 0;
    let feedIdx = 0;
    let compCycleIdx = 0; // cycles through components[] array: 0→1→0→1…
    const defaultInsertAfter = 3;

    items.forEach((item, i) => {
      const capturedFeedIdx = feedIdx;
      feedIdx++;

      const service = item.service?.toLowerCase();
      let card;
      if (service === "twitter") {
        card = (
          <TwitterCard
            key={`rss-${i}`}
            item={item}
            loadCSS={false}
            onClick={() => setSliderIndex(capturedFeedIdx)}
          />
        );
      } else if (service === "instagram") {
        card = (
          <InstagramCard
            key={`rss-${i}`}
            item={item}
            loadCSS={false}
            onClick={() => setSliderIndex(capturedFeedIdx)}
          />
        );
      } else if (service === "youtube") {
        card = (
          <YoutubeCard
            key={`rss-${i}`}
            item={item}
            loadCSS={false}
            onClick={() => setSliderIndex(capturedFeedIdx)}
          />
        );
      } else {
        card = (
          <RssCard
            key={`rss-${i}`}
            item={item}
            loadCSS={false}
            onClick={() => setSliderIndex(capturedFeedIdx)}
          />
        );
      }
      children.push(card);

      // Insert next ad after every `insertAfter` items
      const currentAdConfig =
        adConfig[adIndex] || adConfig[adConfig.length - 1];
      if (currentAdConfig) {
        const insertAfter = currentAdConfig.insertAfter ?? defaultInsertAfter;
        if ((i + 1) % insertAfter === 0) {
          if (currentAdConfig.type === "components") {
            const comps = currentAdConfig.components || [];
            const comp = comps[compCycleIdx % comps.length];
            compCycleIdx++;
            children.push(
              <ComponentAdRenderer
                key={`ad-${i}`}
                name={comp.name}
                componentData={comp.componentData}
                difference={currentAdConfig.difference || "+0"}
                socketConfig={currentAdConfig.socketConfig}
              />
            );
          } else if (currentAdConfig.type === "timeline") {
            children.push(
              <div key={`ad-${i}`} style={{ width: 300, height: 600, overflow: "hidden", flexShrink: 0 }}>
                <MatchTimeline
                  matchId={currentAdConfig.matchId}
                  baseUrl={currentAdConfig.baseUrl}
                  sport={currentAdConfig.sport || "football"}
                  loadCSS={false}
                />
              </div>
            );
          } else {
            children.push(
              <AdWidget
                key={`ad-${i}`}
                ratio={currentAdConfig.ratio}
                src={currentAdConfig.src}
                link={currentAdConfig.link}
                loadCSS={false}
              />
            );
          }
          if (adIndex < adConfig.length - 1) adIndex++;
        }
      }
    });

    return children;
  };

  // ── Compact ratio mode: render slider directly, no grid ──
  if (sliderRatio) {
    return (
      <div className="interactives-host">
        <FeedSlider
          items={items}
          startIndex={0}
          adConfig={adConfig?.[0] || null}
          onNeedMore={fetchMore}
          hasMore={hasMore}
          ratio={sliderRatio}
          showCaption={showCaption}
        />
      </div>
    );
  }

  return (
    <div className="interactives-host">
      <div className="interactives-host__grid-wrap">
        <Grid columns={layout} gap={gap} loadCSS={false}>
          {buildChildren()}
        </Grid>
      </div>

      {sliderIndex !== null && (
        <FeedSlider
          items={items}
          startIndex={sliderIndex}
          onClose={() => setSliderIndex(null)}
          adConfig={adConfig?.[0] || null}
          onNeedMore={fetchMore}
          hasMore={hasMore}
          ratio={sliderRatio}
          showCaption={showCaption}
        />
      )}
    </div>
  );
};

export default InteractivesHost;
